import { useState, useEffect, useRef } from "react";

// ─── STYLES ────────────────────────────────────────────────────────────────
const styles = `
  :root {
    --bg: #060b18;
    --bg2: #0c1428;
    --bg3: #111d38;
    --surface: #0f1a30;
    --border: rgba(56,139,253,0.15);
    --border2: rgba(56,139,253,0.3);
    --accent: #2f80ed;
    --accent2: #00c6a7;
    --accent3: #6e40c9;
    --text: #e8edf5;
    --text2: #8b9dbf;
    --text3: #4a5a7a;
    --glow: rgba(47,128,237,0.4);
    --glow2: rgba(0,198,167,0.3);
    --font-head: 'Syne', sans-serif;
    --font-body: 'DM Sans', sans-serif;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html { scroll-behavior: smooth; }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-body);
    font-size: 16px;
    line-height: 1.6;
    overflow-x: hidden;
  }

  /* ── SCROLLBAR ── */
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 3px; }

  /* ── NOISE OVERLAY ── */
  body::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 9999;
    opacity: 0.4;
  }

  /* ── NAV ── */
  nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5%;
    height: 72px;
    background: rgba(6,11,24,0.85);
    backdrop-filter: blur(20px);
    border-bottom: 1px solid var(--border);
    transition: all 0.3s;
  }
  nav.scrolled {
    height: 60px;
    background: rgba(6,11,24,0.96);
    box-shadow: 0 4px 40px rgba(0,0,0,0.5);
  }

  .nav-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    cursor: pointer;
  }
  .nav-logo-mark {
    width: 38px; height: 38px;
    position: relative;
  }
  .nav-brand {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
  }
  .nav-brand-main {
    font-family: var(--font-head);
    font-weight: 800;
    font-size: 18px;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #fff 0%, var(--accent) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .nav-brand-sub {
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--text3);
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 8px;
    list-style: none;
  }
  .nav-links a {
    text-decoration: none;
    color: var(--text2);
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 8px;
    transition: all 0.2s;
    letter-spacing: 0.01em;
  }
  .nav-links a:hover, .nav-links a.active {
    color: var(--text);
    background: rgba(47,128,237,0.1);
  }
  .nav-cta {
    background: var(--accent) !important;
    color: #fff !important;
    font-weight: 600 !important;
    padding: 10px 20px !important;
    border-radius: 8px !important;
    transition: all 0.2s !important;
    box-shadow: 0 0 20px rgba(47,128,237,0.3);
  }
  .nav-cta:hover {
    background: #4a94ff !important;
    box-shadow: 0 0 30px rgba(47,128,237,0.5) !important;
    transform: translateY(-1px);
  }

  /* Hamburger */
  .hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;
    padding: 8px;
    background: none;
    border: none;
  }
  .hamburger span {
    display: block;
    width: 24px; height: 2px;
    background: var(--text);
    border-radius: 2px;
    transition: all 0.3s;
  }

  /* ── HERO ── */
  .hero {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 120px 5% 80px;
    position: relative;
    overflow: hidden;
    text-align: center;
  }

  .hero-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(47,128,237,0.06) 1px, transparent 1px),
      linear-gradient(90deg, rgba(47,128,237,0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 50%, black 20%, transparent 100%);
  }

  .hero-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
  }
  .hero-orb-1 {
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(47,128,237,0.15) 0%, transparent 70%);
    top: -200px; left: -200px;
  }
  .hero-orb-2 {
    width: 500px; height: 500px;
    background: radial-gradient(circle, rgba(0,198,167,0.12) 0%, transparent 70%);
    bottom: -100px; right: -100px;
  }
  .hero-orb-3 {
    width: 400px; height: 400px;
    background: radial-gradient(circle, rgba(110,64,201,0.1) 0%, transparent 70%);
    top: 50%; left: 60%;
    transform: translate(-50%,-50%);
  }

  .hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 16px;
    background: rgba(47,128,237,0.1);
    border: 1px solid var(--border2);
    border-radius: 100px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 32px;
    animation: fadeInUp 0.6s ease both;
  }
  .badge-dot {
    width: 6px; height: 6px;
    background: var(--accent2);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.3); }
  }

  .hero h1 {
    font-family: var(--font-head);
    font-weight: 800;
    font-size: clamp(42px, 7vw, 88px);
    line-height: 1.0;
    letter-spacing: -0.03em;
    margin-bottom: 28px;
    max-width: 900px;
    animation: fadeInUp 0.6s 0.1s ease both;
  }
  .hero h1 .line-accent {
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-sub {
    font-size: clamp(16px, 2vw, 20px);
    color: var(--text2);
    max-width: 600px;
    margin-bottom: 48px;
    font-weight: 300;
    line-height: 1.7;
    animation: fadeInUp 0.6s 0.2s ease both;
  }

  .hero-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;
    animation: fadeInUp 0.6s 0.3s ease both;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 16px 32px;
    background: var(--accent);
    color: #fff;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 15px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
    box-shadow: 0 0 30px rgba(47,128,237,0.4);
    letter-spacing: 0.01em;
  }
  .btn-primary:hover {
    background: #4a94ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 40px rgba(47,128,237,0.5);
  }
  .btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 15px 32px;
    background: transparent;
    color: var(--text);
    font-family: var(--font-body);
    font-weight: 500;
    font-size: 15px;
    border: 1px solid var(--border2);
    border-radius: 10px;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s;
  }
  .btn-secondary:hover {
    background: rgba(255,255,255,0.05);
    border-color: rgba(56,139,253,0.5);
    transform: translateY(-2px);
  }

  .hero-metrics {
    display: flex;
    gap: 48px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 72px;
    padding-top: 48px;
    border-top: 1px solid var(--border);
    animation: fadeInUp 0.6s 0.4s ease both;
    width: 100%;
    max-width: 700px;
  }
  .metric {
    text-align: center;
  }
  .metric-num {
    font-family: var(--font-head);
    font-size: 32px;
    font-weight: 800;
    background: linear-gradient(135deg, #fff 0%, var(--accent) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .metric-label {
    font-size: 12px;
    color: var(--text3);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-top: 4px;
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ── PIPELINE VISUAL ── */
  .pipeline-strip {
    width: 100%;
    max-width: 800px;
    margin: 56px auto 0;
    animation: fadeInUp 0.6s 0.35s ease both;
  }
  .pipeline-nodes {
    display: flex;
    align-items: center;
    gap: 0;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 16px 24px;
    overflow: hidden;
  }
  .pipeline-node {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    gap: 8px;
    position: relative;
  }
  .pipeline-node::after {
    content: '';
    position: absolute;
    right: 0; top: 50%;
    transform: translateY(-50%);
    width: 1px; height: 40px;
    background: var(--border);
  }
  .pipeline-node:last-child::after { display: none; }
  .p-icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    position: relative;
  }
  .p-icon.ok { background: rgba(0,198,167,0.15); }
  .p-icon.warn { background: rgba(255,180,0,0.15); }
  .p-icon.run { background: rgba(47,128,237,0.15); }
  .p-label { font-size: 10px; color: var(--text3); text-transform: uppercase; letter-spacing: 0.08em; }
  .p-status { font-size: 9px; font-weight: 600; }
  .p-status.ok { color: var(--accent2); }
  .p-status.warn { color: #ffb400; }
  .p-status.run { color: var(--accent); }
  .running-dot {
    width: 6px; height: 6px;
    background: var(--accent);
    border-radius: 50%;
    animation: ping 1.5s infinite;
    position: absolute;
    top: 4px; right: 4px;
  }
  @keyframes ping {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.6); opacity: 0.4; }
  }

  /* ── SECTION SHARED ── */
  section {
    padding: 100px 5%;
    position: relative;
  }
  .section-inner {
    max-width: 1200px;
    margin: 0 auto;
  }
  .section-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 16px;
  }
  .section-label::before {
    content: '';
    display: block;
    width: 20px; height: 1px;
    background: var(--accent);
  }
  .section-title {
    font-family: var(--font-head);
    font-weight: 800;
    font-size: clamp(32px, 4vw, 52px);
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin-bottom: 20px;
  }
  .section-title .accent { color: var(--accent); }
  .section-desc {
    font-size: 18px;
    color: var(--text2);
    max-width: 560px;
    line-height: 1.7;
    font-weight: 300;
  }

  /* ── ABOUT ── */
  .about-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
    margin-top: 64px;
  }
  .about-text p {
    color: var(--text2);
    line-height: 1.8;
    margin-bottom: 20px;
    font-size: 16px;
  }
  .about-text p strong {
    color: var(--text);
    font-weight: 600;
  }
  .about-pillars {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 36px;
  }
  .pillar {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    transition: all 0.2s;
  }
  .pillar:hover {
    border-color: var(--border2);
    background: var(--bg3);
    transform: translateX(4px);
  }
  .pillar-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }
  .pillar-icon.blue { background: rgba(47,128,237,0.15); }
  .pillar-icon.green { background: rgba(0,198,167,0.15); }
  .pillar-icon.purple { background: rgba(110,64,201,0.15); }
  .pillar h4 {
    font-family: var(--font-head);
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 4px;
  }
  .pillar p {
    font-size: 13px;
    color: var(--text2);
    line-height: 1.5;
  }

  .about-visual {
    position: relative;
  }
  .about-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 32px;
    position: relative;
    overflow: hidden;
  }
  .about-card::before {
    content: '';
    position: absolute;
    top: -50px; right: -50px;
    width: 200px; height: 200px;
    background: radial-gradient(circle, rgba(47,128,237,0.15) 0%, transparent 70%);
    border-radius: 50%;
  }
  .about-card-title {
    font-family: var(--font-head);
    font-size: 13px;
    font-weight: 600;
    color: var(--accent2);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .company-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
    font-size: 14px;
  }
  .company-info-row:last-child { border-bottom: none; }
  .company-info-row span:first-child { color: var(--text3); font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; }
  .company-info-row span:last-child { color: var(--text); font-weight: 500; }
  .brand-badge-row {
    margin-top: 24px;
    padding: 16px;
    background: rgba(47,128,237,0.08);
    border: 1px solid rgba(47,128,237,0.2);
    border-radius: 10px;
    text-align: center;
  }
  .brand-badge-label { font-size: 10px; color: var(--text3); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 6px; }
  .brand-badge-name {
    font-family: var(--font-head);
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, var(--accent) 0%, var(--accent2) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
  }
  .brand-badge-tagline { font-size: 11px; color: var(--text3); margin-top: 4px; }

  /* ── SERVICES ── */
  .services-bg {
    background: var(--bg2);
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
  }
  .services-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
    margin-top: 64px;
  }
  .service-card {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 32px;
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
    cursor: default;
  }
  .service-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    opacity: 0;
    transition: opacity 0.3s;
    background: linear-gradient(135deg, rgba(47,128,237,0.05) 0%, transparent 100%);
  }
  .service-card:hover {
    border-color: var(--border2);
    transform: translateY(-4px);
    box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  }
  .service-card:hover::before { opacity: 1; }
  .service-card.featured {
    border-color: rgba(0,198,167,0.3);
    background: linear-gradient(135deg, rgba(0,198,167,0.05) 0%, var(--bg) 100%);
  }
  .service-card.featured::after {
    content: 'Most Popular';
    position: absolute;
    top: 16px; right: 16px;
    background: var(--accent2);
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 100px;
  }
  .service-icon {
    width: 56px; height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    margin-bottom: 24px;
  }
  .service-icon.blue { background: rgba(47,128,237,0.15); }
  .service-icon.green { background: rgba(0,198,167,0.15); }
  .service-icon.purple { background: rgba(110,64,201,0.15); }
  .service-card h3 {
    font-family: var(--font-head);
    font-weight: 700;
    font-size: 20px;
    margin-bottom: 12px;
    letter-spacing: -0.01em;
  }
  .service-card > p {
    font-size: 14px;
    color: var(--text2);
    line-height: 1.7;
    margin-bottom: 24px;
  }
  .service-features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 28px;
  }
  .service-features li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    color: var(--text2);
    line-height: 1.4;
  }
  .service-features li::before {
    content: '✓';
    color: var(--accent2);
    font-weight: 700;
    font-size: 12px;
    margin-top: 1px;
    flex-shrink: 0;
  }
  .service-cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--accent);
    text-decoration: none;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    transition: gap 0.2s;
  }
  .service-cta:hover { gap: 10px; }

  /* ── PROCESS ── */
  .process-steps {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    margin-top: 64px;
    position: relative;
  }
  .process-steps::before {
    content: '';
    position: absolute;
    top: 28px; left: 12.5%; right: 12.5%;
    height: 1px;
    background: linear-gradient(90deg, var(--accent), var(--accent2));
    opacity: 0.3;
  }
  .process-step {
    text-align: center;
    padding: 0 20px;
    position: relative;
  }
  .step-num {
    width: 56px; height: 56px;
    border-radius: 50%;
    background: var(--bg2);
    border: 1px solid var(--border2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-head);
    font-weight: 800;
    font-size: 18px;
    color: var(--accent);
    margin: 0 auto 20px;
    position: relative;
    z-index: 1;
    background: var(--bg);
  }
  .process-step h4 {
    font-family: var(--font-head);
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 8px;
  }
  .process-step p {
    font-size: 13px;
    color: var(--text2);
    line-height: 1.6;
  }

  /* ── CONTACT ── */
  .contact-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    margin-top: 64px;
    align-items: start;
  }
  .contact-info h3 {
    font-family: var(--font-head);
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 12px;
  }
  .contact-info p {
    color: var(--text2);
    line-height: 1.7;
    font-size: 15px;
    margin-bottom: 36px;
  }
  .contact-detail {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    margin-bottom: 12px;
    transition: all 0.2s;
  }
  .contact-detail:hover {
    border-color: var(--border2);
    background: var(--bg3);
  }
  .contact-detail-icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    background: rgba(47,128,237,0.12);
    flex-shrink: 0;
  }
  .contact-detail-text span {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text3);
    margin-bottom: 2px;
  }
  .contact-detail-text a,
  .contact-detail-text strong {
    font-size: 14px;
    font-weight: 500;
    color: var(--text);
    text-decoration: none;
  }
  .contact-detail-text a:hover { color: var(--accent); }

  /* ── FORM ── */
  .contact-form {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 40px;
  }
  .form-title {
    font-family: var(--font-head);
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 28px;
  }
  .form-group {
    margin-bottom: 20px;
  }
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text2);
    margin-bottom: 8px;
  }
  input, textarea, select {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px 16px;
    color: var(--text);
    font-family: var(--font-body);
    font-size: 14px;
    transition: border-color 0.2s;
    outline: none;
    resize: none;
  }
  input:focus, textarea:focus, select:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(47,128,237,0.1);
  }
  input::placeholder, textarea::placeholder {
    color: var(--text3);
  }
  select option {
    background: var(--bg2);
  }
  .form-submit {
    width: 100%;
    padding: 16px;
    background: var(--accent);
    color: #fff;
    font-family: var(--font-body);
    font-size: 15px;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 0 20px rgba(47,128,237,0.3);
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .form-submit:hover {
    background: #4a94ff;
    box-shadow: 0 0 30px rgba(47,128,237,0.5);
    transform: translateY(-1px);
  }
  .form-submit:disabled {
    background: var(--text3);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  .success-msg {
    background: rgba(0,198,167,0.1);
    border: 1px solid rgba(0,198,167,0.3);
    border-radius: 10px;
    padding: 16px;
    text-align: center;
    color: var(--accent2);
    font-size: 14px;
    margin-top: 16px;
    animation: fadeInUp 0.4s ease;
  }
  .error-msg {
    background: rgba(255,80,80,0.1);
    border: 1px solid rgba(255,80,80,0.3);
    border-radius: 10px;
    padding: 16px;
    text-align: center;
    color: #ff8080;
    font-size: 14px;
    margin-top: 16px;
    animation: fadeInUp 0.4s ease;
  }

  /* ── FOOTER ── */
  footer {
    background: var(--bg2);
    border-top: 1px solid var(--border);
    padding: 60px 5% 32px;
  }
  .footer-inner {
    max-width: 1200px;
    margin: 0 auto;
  }
  .footer-top {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 60px;
    margin-bottom: 48px;
  }
  .footer-brand p {
    font-size: 14px;
    color: var(--text3);
    line-height: 1.7;
    margin-top: 16px;
    max-width: 300px;
  }
  .footer-col h4 {
    font-family: var(--font-head);
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--text2);
    margin-bottom: 16px;
  }
  .footer-col ul { list-style: none; }
  .footer-col ul li { margin-bottom: 10px; }
  .footer-col ul li a, .footer-col ul li button {
    font-size: 14px;
    color: var(--text3);
    text-decoration: none;
    background: none; border: none;
    cursor: pointer;
    font-family: var(--font-body);
    padding: 0;
    transition: color 0.2s;
  }
  .footer-col ul li a:hover, .footer-col ul li button:hover { color: var(--text); }
  .footer-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 24px;
    border-top: 1px solid var(--border);
    flex-wrap: wrap;
    gap: 12px;
  }
  .footer-bottom p { font-size: 12px; color: var(--text3); }
  .footer-bottom a { color: var(--text3); text-decoration: none; transition: color 0.2s; }
  .footer-bottom a:hover { color: var(--accent); }

  /* ── MOBILE ── */
  @media (max-width: 900px) {
    .nav-links { display: none; }
    .nav-links.open {
      display: flex;
      flex-direction: column;
      position: fixed;
      top: 60px; left: 0; right: 0;
      background: var(--bg);
      padding: 16px;
      border-bottom: 1px solid var(--border);
      gap: 4px;
    }
    .hamburger { display: flex; }
    .about-grid, .contact-grid { grid-template-columns: 1fr; gap: 40px; }
    .services-grid { grid-template-columns: 1fr; }
    .process-steps { grid-template-columns: 1fr 1fr; gap: 32px; }
    .process-steps::before { display: none; }
    .footer-top { grid-template-columns: 1fr; gap: 32px; }
    .form-row { grid-template-columns: 1fr; }
    .pipeline-nodes { padding: 12px 16px; }
    .p-label { display: none; }
    .hero-metrics { gap: 24px; }
  }

  @media (max-width: 580px) {
    .hero h1 { font-size: 36px; }
    .hero-actions { flex-direction: column; align-items: center; }
    .hero-actions .btn-primary, .hero-actions .btn-secondary { width: 100%; max-width: 320px; justify-content: center; }
    .process-steps { grid-template-columns: 1fr; }
    .contact-form { padding: 24px; }
  }
`;

// ─── LOGO SVG ───────────────────────────────────────────────────────────────
const Logo = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <defs>
      <linearGradient id="lg1" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#2f80ed" />
        <stop offset="100%" stopColor="#00c6a7" />
      </linearGradient>
      <linearGradient id="lg2" x1="0" y1="40" x2="40" y2="0" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#6e40c9" />
        <stop offset="100%" stopColor="#2f80ed" />
      </linearGradient>
    </defs>
    {/* Hexagon outer */}
    <path d="M20 2 L35 11 L35 29 L20 38 L5 29 L5 11 Z" stroke="url(#lg1)" strokeWidth="1.5" fill="none" />
    {/* Flow lines */}
    <path d="M12 20 L20 14 L28 20 L20 26 Z" fill="url(#lg1)" opacity="0.8" />
    {/* Shield inner */}
    <path d="M20 10 L27 13.5 L27 21 Q27 26 20 29 Q13 26 13 21 L13 13.5 Z"
      fill="none" stroke="url(#lg2)" strokeWidth="1.2" opacity="0.6" />
    {/* Center dot */}
    <circle cx="20" cy="20" r="2.5" fill="url(#lg1)" />
  </svg>
);

// ─── NAV COMPONENT ──────────────────────────────────────────────────────────
function Nav({ activePage, setActivePage }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navTo = (page) => { setActivePage(page); setMenuOpen(false); window.scrollTo(0, 0); };

  return (
    <nav className={scrolled ? "scrolled" : ""}>
      <div className="nav-logo" onClick={() => navTo("home")}>
        <Logo size={36} />
        <div className="nav-brand">
          <span className="nav-brand-main">SecureFlow</span>
          <span className="nav-brand-sub">by ITChore Inc</span>
        </div>
      </div>
      <ul className={`nav-links${menuOpen ? " open" : ""}`}>
        {["home", "about", "services", "contact"].map(p => (
          <li key={p}>
            <a
              className={activePage === p ? "active" : ""}
              onClick={(e) => { e.preventDefault(); navTo(p); }}
              href="#"
            >{p.charAt(0).toUpperCase() + p.slice(1)}</a>
          </li>
        ))}
        <li>
          <a className="nav-cta" onClick={(e) => { e.preventDefault(); navTo("contact"); }} href="#">
            Get in Touch
          </a>
        </li>
      </ul>
      <button className="hamburger" onClick={() => setMenuOpen(o => !o)}>
        <span /><span /><span />
      </button>
    </nav>
  );
}

// ─── HOME PAGE ──────────────────────────────────────────────────────────────
function HomePage({ setActivePage }) {
  return (
    <>
      <section className="hero">
        <div className="hero-grid" />
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="hero-orb hero-orb-3" />

        <div className="hero-badge">
          <span className="badge-dot" />
          DevOps & DevSecOps Consultancy
        </div>

        <h1>
          Ship Fast.<br />
          <span className="line-accent">Stay Secure.</span><br />
          No Compromises.
        </h1>

        <p className="hero-sub">
          SecureFlow helps engineering teams embed security into every stage of their pipeline —
          without slowing down delivery. Hands-on expertise, zero agency overhead.
        </p>

        <div className="hero-actions">
          <button className="btn-primary" onClick={() => { setActivePage("contact"); window.scrollTo(0,0); }}>
            Start a Conversation →
          </button>
          <button className="btn-secondary" onClick={() => { setActivePage("services"); window.scrollTo(0,0); }}>
            Explore Services
          </button>
        </div>

        {/* Live Pipeline */}
        <div className="pipeline-strip">
          <div className="pipeline-nodes">
            {[
              { icon: "💻", label: "Commit", status: "ok", statusTxt: "Clean" },
              { icon: "🔍", label: "SAST Scan", status: "ok", statusTxt: "Passed" },
              { icon: "📦", label: "Build", status: "run", statusTxt: "Running", dot: true },
              { icon: "🛡️", label: "Security", status: "warn", statusTxt: "2 Flagged" },
              { icon: "🚀", label: "Deploy", status: "run", statusTxt: "Pending" },
            ].map((n, i) => (
              <div className="pipeline-node" key={i}>
                <div className={`p-icon ${n.status}`}>
                  {n.icon}
                  {n.dot && <span className="running-dot" />}
                </div>
                <div className="p-label">{n.label}</div>
                <div className={`p-status ${n.status}`}>{n.statusTxt}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-metrics">
          {[
            { num: "100%", label: "Security-First" },
            { num: "24/7", label: "Support Available" },
            { num: "0", label: "Agency Overhead" },
          ].map((m, i) => (
            <div className="metric" key={i}>
              <div className="metric-num">{m.num}</div>
              <div className="metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

// ─── ABOUT PAGE ─────────────────────────────────────────────────────────────
function AboutPage() {
  return (
    <section style={{ paddingTop: 120 }}>
      <div className="section-inner">
        <div className="section-label">Our Story</div>
        <h2 className="section-title">Built by practitioners,<br /><span className="accent">for practitioners.</span></h2>
        <p className="section-desc">
          SecureFlow was founded by a DevOps & DevSecOps professional who spent years embedded in
          engineering teams — and realized that most consultancies bring slide decks, not solutions.
        </p>

        <div className="about-grid">
          <div className="about-text">
            <p>
              <strong>SecureFlow</strong> is the client-facing brand of <strong>ITChore Inc.</strong> —
              an independent consultancy focused on practical, production-ready DevOps and DevSecOps
              engagements. We don't believe in bloated project teams or months-long discovery phases.
            </p>
            <p>
              We come in, understand your stack, and get to work. Whether you need a CI/CD pipeline
              secured from the ground up, a path to SOC 2 compliance, or someone to embed with your
              team during a critical infrastructure migration — we've been there.
            </p>
            <p>
              As a lean, solo-led consultancy, you get direct access to the expert on day one.
              No account managers, no junior handoffs, no overhead.
            </p>

            <div className="about-pillars">
              {[
                { icon: "⚡", cls: "blue", title: "Speed without shortcuts", desc: "We move fast by automating the right things — not by skipping security gates." },
                { icon: "🛡️", cls: "green", title: "Security as a first class citizen", desc: "Security isn't a checklist at the end of the sprint. It's baked into every stage of your pipeline." },
                { icon: "🔄", cls: "purple", title: "Continuous improvement", desc: "We build systems that get better over time — and leave your team equipped to own them." },
              ].map((p, i) => (
                <div className="pillar" key={i}>
                  <div className={`pillar-icon ${p.cls}`}>{p.icon}</div>
                  <div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="about-visual">
            <div className="about-card">
              <div className="about-card-title">
                <span>🏢</span> Corporate Structure
              </div>
              {[
                { label: "Legal Entity", val: "ITChore Inc." },
                { label: "Client Brand", val: "SecureFlow" },
                { label: "Service Area", val: "DevOps · DevSecOps" },
                { label: "Engagement Model", val: "Project / Retainer" },
                { label: "Contact", val: "info@itchore.com" },
              ].map((r, i) => (
                <div className="company-info-row" key={i}>
                  <span>{r.label}</span>
                  <span>{r.val}</span>
                </div>
              ))}
              <div className="brand-badge-row">
                <div className="brand-badge-label">Client-facing brand</div>
                <div className="brand-badge-name">SecureFlow</div>
                <div className="brand-badge-tagline">Ship fast. Stay secure. No compromises.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── SERVICES PAGE ──────────────────────────────────────────────────────────
function ServicesPage({ setActivePage }) {
  const services = [
    {
      icon: "⚙️", cls: "blue",
      title: "DevOps Consulting",
      desc: "End-to-end pipeline design, CI/CD automation, and infrastructure-as-code for engineering teams that need to move faster without breaking things.",
      features: [
        "CI/CD pipeline design & implementation",
        "Infrastructure as Code (Terraform, Pulumi)",
        "Container orchestration (Kubernetes, Docker)",
        "Cloud architecture (AWS / GCP / Azure)",
        "Monitoring & observability setup",
        "Developer experience improvements",
      ],
    },
    {
      icon: "🛡️", cls: "green", featured: true,
      title: "DevSecOps Integration",
      desc: "Shift security left — embed automated security scanning, policy enforcement, and compliance guardrails directly into your development workflow.",
      features: [
        "SAST / DAST / SCA tooling setup",
        "Secrets management & vault integration",
        "Security policy as code",
        "Vulnerability triage & remediation",
        "Security gate configuration in pipelines",
        "Incident response runbook creation",
      ],
    },
    {
      icon: "✅", cls: "purple",
      title: "Compliance & Audit Readiness",
      desc: "Whether you're heading toward SOC 2, ISO 27001, or a customer security review — we help you get your technical controls in order, fast.",
      features: [
        "SOC 2 Type I / II readiness",
        "Evidence collection automation",
        "Control mapping to your stack",
        "Pre-audit technical gap analysis",
        "Continuous compliance monitoring",
        "Audit documentation support",
      ],
    },
  ];

  return (
    <>
      <section className="services-bg" style={{ paddingTop: 120 }}>
        <div className="section-inner">
          <div className="section-label">What We Do</div>
          <h2 className="section-title">Services built for<br /><span className="accent">real engineering teams.</span></h2>
          <p className="section-desc">
            No fluff. No bloated engagements. We offer focused, outcome-oriented services
            that map directly to what your team needs.
          </p>

          <div className="services-grid">
            {services.map((s, i) => (
              <div className={`service-card${s.featured ? " featured" : ""}`} key={i}>
                <div className={`service-icon ${s.cls}`}>{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul className="service-features">
                  {s.features.map((f, j) => <li key={j}>{f}</li>)}
                </ul>
                <button className="service-cta" onClick={() => { setActivePage("contact"); window.scrollTo(0,0); }}>
                  Enquire about this service <span>→</span>
                </button>
              </div>
            ))}
          </div>

          {/* Process */}
          <div style={{ marginTop: 100 }}>
            <div className="section-label">How We Work</div>
            <h2 className="section-title" style={{ marginBottom: 0 }}>Our engagement <span className="accent">process.</span></h2>
            <div className="process-steps">
              {[
                { n: "01", title: "Discovery Call", desc: "30-minute conversation to understand your stack, pain points, and goals." },
                { n: "02", title: "Assessment", desc: "We review your current setup and identify the highest-impact opportunities." },
                { n: "03", title: "Engagement", desc: "Hands-on work begins. You have direct access to the consultant throughout." },
                { n: "04", title: "Handoff", desc: "Documentation, knowledge transfer, and a clear path forward for your team." },
              ].map((s, i) => (
                <div className="process-step" key={i}>
                  <div className="step-num">{s.n}</div>
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── CONTACT PAGE ────────────────────────────────────────────────────────────
function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'sending' | 'success' | 'error'

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.message) {
      setStatus("error_fields");
      return;
    }
    setStatus("sending");
    try {
      // Use mailto as the primary contact mechanism (works without backend)
      const subject = encodeURIComponent(`[SecureFlow Enquiry] ${form.service || "General"} — ${form.name}`);
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company || "—"}\nService Interest: ${form.service || "—"}\n\nMessage:\n${form.message}`
      );
      window.open(`mailto:info@itchore.com?subject=${subject}&body=${body}`, "_blank");
      setStatus("success");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section style={{ paddingTop: 120 }}>
      <div className="section-inner">
        <div className="section-label">Get In Touch</div>
        <h2 className="section-title">Let's build something<br /><span className="accent">secure together.</span></h2>
        <p className="section-desc">
          Whether you have a specific project in mind or just want to explore what's possible —
          reach out. We respond within 24 hours.
        </p>

        <div className="contact-grid">
          <div className="contact-info">
            <h3>Talk to us directly</h3>
            <p>
              SecureFlow engagements start with a conversation. No forms to fill, no waiting weeks
              for a proposal. Tell us what you're working on and we'll tell you if we can help.
            </p>

            {[
              { icon: "📧", label: "Email", val: <a href="mailto:info@itchore.com">info@itchore.com</a> },
              { icon: "🏢", label: "Company", val: <strong>ITChore Inc.</strong> },
              { icon: "🌐", label: "Brand", val: <strong>SecureFlow</strong> },
              { icon: "📍", label: "Availability", val: <strong>Remote-first · Worldwide</strong> },
            ].map((d, i) => (
              <div className="contact-detail" key={i}>
                <div className="contact-detail-icon">{d.icon}</div>
                <div className="contact-detail-text">
                  <span>{d.label}</span>
                  {d.val}
                </div>
              </div>
            ))}
          </div>

          <div className="contact-form">
            <div className="form-title">Send us a message</div>
            <div className="form-row">
              <div className="form-group">
                <label>Your Name *</label>
                <input name="name" value={form.name} onChange={handleChange} placeholder="Jane Smith" />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@company.com" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Company</label>
                <input name="company" value={form.company} onChange={handleChange} placeholder="Acme Corp" />
              </div>
              <div className="form-group">
                <label>Service Interest</label>
                <select name="service" value={form.service} onChange={handleChange}>
                  <option value="">— Select a service —</option>
                  <option>DevOps Consulting</option>
                  <option>DevSecOps Integration</option>
                  <option>Compliance & Audit Readiness</option>
                  <option>General Enquiry</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Message *</label>
              <textarea name="message" rows={5} value={form.message} onChange={handleChange}
                placeholder="Tell us about your project, team size, or what challenge you're facing..." />
            </div>
            <button className="form-submit" onClick={handleSubmit} disabled={status === "sending"}>
              {status === "sending" ? "Opening email client..." : "Send Message → info@itchore.com"}
            </button>
            {status === "success" && (
              <div className="success-msg">
                ✓ Your email client has opened with your message pre-filled. Please send it to complete your enquiry.
              </div>
            )}
            {status === "error_fields" && (
              <div className="error-msg">Please fill in your name, email, and message before sending.</div>
            )}
            {status === "error" && (
              <div className="error-msg">Something went wrong. Please email us directly at info@itchore.com</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FOOTER ─────────────────────────────────────────────────────────────────
function Footer({ setActivePage }) {
  const navTo = (p) => { setActivePage(p); window.scrollTo(0, 0); };
  return (
    <footer>
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="nav-logo" style={{ cursor: "pointer" }} onClick={() => navTo("home")}>
              <Logo size={32} />
              <div className="nav-brand">
                <span className="nav-brand-main">SecureFlow</span>
                <span className="nav-brand-sub">by ITChore Inc</span>
              </div>
            </div>
            <p>
              DevOps & DevSecOps consultancy helping engineering teams ship fast and stay secure.
              Hands-on expertise, zero agency overhead.
            </p>
          </div>
          <div className="footer-col">
            <h4>Navigation</h4>
            <ul>
              {["home", "about", "services", "contact"].map(p => (
                <li key={p}><button onClick={() => navTo(p)}>{p.charAt(0).toUpperCase() + p.slice(1)}</button></li>
              ))}
            </ul>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <ul>
              <li><a href="mailto:info@itchore.com">info@itchore.com</a></li>
              <li><span style={{ fontSize: 13, color: "var(--text3)" }}>ITChore Inc.</span></li>
              <li><span style={{ fontSize: 13, color: "var(--text3)" }}>Remote-first · Worldwide</span></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} ITChore Inc. All rights reserved. SecureFlow is a brand of ITChore Inc.</p>
          <p>
            <a href="mailto:info@itchore.com">info@itchore.com</a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── APP ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [activePage, setActivePage] = useState("home");

  return (
    <>
      <style>{styles}</style>
      <Nav activePage={activePage} setActivePage={setActivePage} />
      <main>
        {activePage === "home" && <HomePage setActivePage={setActivePage} />}
        {activePage === "about" && <AboutPage />}
        {activePage === "services" && <ServicesPage setActivePage={setActivePage} />}
        {activePage === "contact" && <ContactPage />}
      </main>
      <Footer setActivePage={setActivePage} />
    </>
  );
}
